import { useState } from 'react'
import { Plus, Pencil, ShieldAlert } from 'lucide-react'
import InfoTooltip from './InfoTooltip'
import UserPicker from './UserPicker'
import { inputClass } from './Field'
import type { RiskEntry, RiskLevel, RiskCategory, RiskStatus, UserRef } from '../types'
import {
  RISK_LEVELS, RISK_LEVEL_LABEL, IMPACT_HINTS, LIKELIHOOD_HINTS,
  riskScore, ragForScore, riskRag, ragCounts,
  RAG_BAR, RAG_PILL, RAG_LABEL,
} from '../lib/risk'

const CATEGORIES: { value: RiskCategory; label: string; hint: string }[] = [
  { value: 'delivery', label: 'Delivery', hint: 'timeline / go-live at risk' },
  { value: 'scope', label: 'Scope', hint: 'may not deliver in full' },
  { value: 'cost', label: 'Cost', hint: 'may exceed forecast cost' },
  { value: 'other', label: 'Other', hint: 'anything else worth flagging' },
]

const statusPill: Record<RiskStatus, string> = {
  open: 'bg-[#EF3340]/10 text-[#EF3340]',
  mitigated: 'bg-[#F88806]/10 text-[#B8640B]',
  closed: 'bg-slate-100 text-slate-500',
}

type Draft = {
  title: string
  impact: RiskLevel
  likelihood: RiskLevel
  category: RiskCategory
  owner: UserRef | undefined
  mitigation: string
  deadline: string
  note: string
}
const EMPTY_DRAFT: Draft = { title: '', impact: 'low', likelihood: 'low', category: 'delivery', owner: undefined, mitigation: '', deadline: '', note: '' }

// Pre-matrix risks only carry the legacy 3-level severity. When one is
// edited, prefill impact from it so the re-assessment starts sensibly.
const LEGACY_IMPACT: Record<string, RiskLevel> = { low: 'low', medium: 'high', high: 'very_high' }

/**
 * Risks list on ProjectDetail, following the Danone risk assessment model:
 * impact x likelihood produce a 1-16 rating banded red/amber/green
 * (lib/risk.ts). Mitigation (risk response) is mandatory - at minimum
 * "working on a mitigation plan, due <date>". The whole array is persisted
 * through the normal project PATCH (parent owns the save); this component
 * only edits a local copy and hands the new array up. Open risks feed the
 * Forecast view on Reports by category (delivery->On Time, scope->In Full,
 * cost->At Cost) once they clear the materiality threshold (see lib/risk).
 */
export default function RisksCard({ risks, currentUser, onSave }: {
  risks: RiskEntry[]
  currentUser?: UserRef
  onSave: (risks: RiskEntry[]) => void
}) {
  const [editingId, setEditingId] = useState<string | null>(null) // 'new' = add form
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)

  const counts = ragCounts(risks)
  const openCount = risks.filter(r => r.status === 'open').length

  // New risks default their owner to the person logging it - usually correct,
  // and one click to reassign when it isn't.
  const startAdd = () => { setDraft({ ...EMPTY_DRAFT, owner: currentUser }); setEditingId('new') }
  const startEdit = (r: RiskEntry) => {
    setDraft({
      title: r.title,
      impact: r.impact ?? (r.severity ? LEGACY_IMPACT[r.severity] : 'low'),
      likelihood: r.likelihood ?? 'low',
      category: r.category,
      owner: r.owner,
      mitigation: r.mitigation ?? '',
      deadline: r.deadline ?? '',
      note: r.note ?? '',
    })
    setEditingId(r.id)
  }

  const draftValid = !!draft.title.trim() && !!draft.mitigation.trim()

  const submitDraft = () => {
    if (!draftValid) return
    const now = new Date().toISOString()
    const assessed = {
      title: draft.title.trim(),
      impact: draft.impact,
      likelihood: draft.likelihood,
      category: draft.category,
      owner: draft.owner,
      mitigation: draft.mitigation.trim(),
      deadline: draft.deadline || undefined,
      note: draft.note.trim() || undefined,
    }
    if (editingId === 'new') {
      const entry: RiskEntry = {
        id: `risk_${Math.random().toString(36).slice(2, 10)}`,
        ...assessed,
        status: 'open',
        createdBy: currentUser,
        createdAt: now,
      }
      onSave([...risks, entry])
    } else {
      // Editing re-assesses the risk on the matrix, so drop the legacy severity.
      onSave(risks.map(r => r.id === editingId
        ? { ...r, ...assessed, severity: undefined, updatedAt: now }
        : r))
    }
    setEditingId(null)
  }

  const setStatus = (id: string, status: RiskStatus) =>
    onSave(risks.map(r => r.id === id ? { ...r, status, updatedAt: new Date().toISOString() } : r))

  const removeRisk = (id: string) => {
    onSave(risks.filter(r => r.id !== id))
    setEditingId(null)
  }

  const draftScore = riskScore(draft)!
  const draftRag = ragForScore(draftScore)

  const form = (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      <input
        autoFocus
        value={draft.title}
        onChange={e => setDraft({ ...draft, title: e.target.value })}
        className={inputClass}
        placeholder="What could go wrong? e.g. Vendor delay on data migration"
      />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Impact</label>
          <select value={draft.impact} onChange={e => setDraft({ ...draft, impact: e.target.value as RiskLevel })} className={inputClass}>
            {RISK_LEVELS.map(l => <option key={l} value={l}>{RISK_LEVEL_LABEL[l]} - {IMPACT_HINTS[l]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Likelihood</label>
          <select value={draft.likelihood} onChange={e => setDraft({ ...draft, likelihood: e.target.value as RiskLevel })} className={inputClass}>
            {RISK_LEVELS.map(l => <option key={l} value={l}>{RISK_LEVEL_LABEL[l]} - {LIKELIHOOD_HINTS[l]}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Category</label>
          <select value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value as RiskCategory })} className={inputClass}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label} - {c.hint}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 pb-1.5">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Risk rating:</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${RAG_PILL[draftRag]}`}>{draftScore} · {RAG_LABEL[draftRag]}</span>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Mitigation / risk response <span className="text-[#EF3340]">*</span></label>
        <textarea
          value={draft.mitigation}
          onChange={e => setDraft({ ...draft, mitigation: e.target.value })}
          className={`${inputClass} min-h-[60px]`}
          placeholder="How will this be handled? At minimum: working on a mitigation plan, due dd/mm/yyyy"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Owner</label>
          <UserPicker value={draft.owner} onChange={u => setDraft({ ...draft, owner: u })} placeholder="Assign an owner" allowFreeText />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Mitigation deadline</label>
          <input type="date" value={draft.deadline} onChange={e => setDraft({ ...draft, deadline: e.target.value })} className={inputClass} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Notes / history</label>
        <input
          value={draft.note}
          onChange={e => setDraft({ ...draft, note: e.target.value })}
          className={inputClass}
          placeholder="Context (optional)"
        />
      </div>
      <div className="flex items-center gap-2">
        <button onClick={submitDraft} disabled={!draftValid} className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          {editingId === 'new' ? 'Add Risk' : 'Save Changes'}
        </button>
        <button onClick={() => setEditingId(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
        {editingId !== 'new' && (
          <button onClick={() => removeRisk(editingId!)} className="ml-auto px-3 py-2 rounded-lg text-sm font-semibold text-[#EF3340] hover:bg-[#EF3340]/10">Delete</button>
        )}
      </div>
    </div>
  )

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-800 flex items-center gap-1.5">
          <ShieldAlert className="w-4 h-4 text-slate-500" />
          Risks
          <InfoTooltip>
            <strong>Project risks</strong>
            <p>Assessed on the Danone matrix: Impact x Likelihood gives a 1-16 rating, banded Red (9+), Amber (4-8) or Green (1-3).</p>
            <p>Every risk needs a mitigation (risk response) - at minimum "working on a mitigation plan" with a date.</p>
            <p>Open risks feed the Forecast view on Reports by category - Delivery affects On Time, Scope affects In Full, Cost affects At Cost - once they're serious enough (Amber+ by default).</p>
          </InfoTooltip>
          {openCount > 0 && (
            <span className="flex items-center gap-1 ml-1">
              {(['red', 'amber', 'green'] as const).map(band => counts[band] > 0 && (
                <span key={band} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${RAG_PILL[band]}`}>{counts[band]} {RAG_LABEL[band].toLowerCase()}</span>
              ))}
            </span>
          )}
        </h3>
        {editingId === null && (
          <button onClick={startAdd} className="flex items-center gap-1.5 text-sm font-semibold text-danone-blue hover:text-[#004a93]">
            <Plus className="w-4 h-4" /> Add Risk
          </button>
        )}
      </div>

      {editingId === 'new' && <div className="mb-3">{form}</div>}

      {risks.length === 0 && editingId === null ? (
        <p className="text-sm text-slate-400 text-center py-4">No risks logged - nothing threatening delivery, scope or cost.</p>
      ) : (
        <div className="space-y-2">
          {risks.map(r => {
            if (editingId === r.id) return <div key={r.id}>{form}</div>
            const score = riskScore(r)
            const rag = riskRag(r)
            return (
              <div key={r.id} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border ${r.status === 'closed' ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-slate-50 border-slate-200'}`}>
                <div className={`w-1.5 h-10 ${rag ? RAG_BAR[rag] : 'bg-slate-300'} rounded-full flex-shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-700">{r.title}</span>
                    {score !== null && rag ? (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${RAG_PILL[rag]}`}>{score} · {RAG_LABEL[rag]}</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500" title="Logged before the risk matrix - edit to assess impact and likelihood">Not assessed</span>
                    )}
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{CATEGORIES.find(c => c.value === r.category)?.label}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${statusPill[r.status]}`}>{r.status}</span>
                  </div>
                  {r.impact && r.likelihood && (
                    <div className="text-xs text-slate-500 mt-0.5">Impact: {RISK_LEVEL_LABEL[r.impact]} · Likelihood: {RISK_LEVEL_LABEL[r.likelihood]}</div>
                  )}
                  {r.owner && (
                    <div className="text-xs text-slate-500 mt-0.5">Owner: <span className="font-medium text-slate-600">{r.owner.name || r.owner.email}</span></div>
                  )}
                  {r.mitigation && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="font-semibold text-slate-600">Mitigation:</span> {r.mitigation}
                      {r.deadline && <span className="text-slate-400"> - due {new Date(r.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    </div>
                  )}
                  {r.note && <div className="text-xs text-slate-500 mt-0.5">{r.note}</div>}
                  <div className="text-[10px] text-slate-400 mt-1">
                    Raised {new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {r.createdBy?.name ? ` by ${r.createdBy.name}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {r.status === 'open' && (
                    <button onClick={() => setStatus(r.id, 'mitigated')} className="px-2 py-1 rounded text-[11px] font-semibold text-[#B8640B] hover:bg-[#F88806]/10">Mitigate</button>
                  )}
                  {r.status !== 'closed' && (
                    <button onClick={() => setStatus(r.id, 'closed')} className="px-2 py-1 rounded text-[11px] font-semibold text-slate-500 hover:bg-slate-100">Close</button>
                  )}
                  {r.status !== 'open' && (
                    <button onClick={() => setStatus(r.id, 'open')} className="px-2 py-1 rounded text-[11px] font-semibold text-[#B8242E] hover:bg-[#EF3340]/10">Reopen</button>
                  )}
                  <button onClick={() => startEdit(r)} title="Edit risk" className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
